import React, { useEffect, useMemo, useState } from 'react'
import {
	Autocomplete, Box, Checkbox, Chip, TextField, Tooltip, Typography,
} from '@mui/material'
import { AppNote } from '@components'
import { dsColorNew } from '@palette'
import { CheckBox as CheckBoxIcon, CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon, BlockOutlined as BlockIcon } from 'Pages/DsRepricing/@components/icons'
import { BACKEND_APIS } from '@constants'

/**
 * One picker for "which offices does this rule reach".
 *
 * Two screens ask that question and until 2026-09-24 they asked two
 * different tables, neither of which was the one the engine reads. The
 * Strategies wizard offered Price Seeker's own office groups — a
 * user-level concept the engine has never heard of, and empty for
 * canarias.com, so the field just looked broken. The Schedule drawer
 * offered every office while its own validator accepts only the ones
 * with a Price Seeker mapping, four of fifty-eight.
 *
 * So: one component, one endpoint, and the vocabulary the engine
 * matches against. Groups and offices in the same list because they
 * are the same axis — the engine expands a group into its offices and
 * then compares office codes either way.
 *
 * `requireMapped` is the only difference between the two callers, and
 * it is a real difference rather than a setting. A STRATEGY can be
 * scoped to any office: the engine compares `dim_location.code` and
 * never needs the Price Seeker id. A SCHEDULE rule launches searches,
 * and a search is addressed by Price Seeker id, so an unmapped office
 * there is a rule that silently produces nothing.
 *
 * Unmapped offices are shown disabled rather than hidden, with the
 * reason and the screen that fixes it. An office that exists and is
 * missing from the list is a mystery; one that is listed and struck
 * through is an instruction.
 */

const ENDPOINT = `${BACKEND_APIS.GENERAL_REPRICING_SETUP_API}/office-scope`

const GROUPS = 'Office groups'

export function labelOf(item) {
	if (item.kind === 'group') {
		const n = item.offices
		// The count is what makes a two-letter code legible: "HO" means
		// nothing on its own.
		return `${item.name} (${item.code}) · ${n} office${n === 1 ? '' : 's'}`
	}
	return item.name === item.code ? item.code : `${item.name} (${item.code})`
}

export default function OfficeScopePicker({
	value, onChange, requireMapped = false, label = 'Offices', placeholder,
	helperText, sx,
}) {
	const [items, setItems] = useState([])
	const [state, setState] = useState('loading')

	useEffect(() => {
		let alive = true
		fetch(ENDPOINT, { credentials: 'include' })
			.then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
			.then(body => { if (alive) { setItems(body?.items || []); setState('ready') } })
			.catch(() => { if (alive) { setItems([]); setState('error') } })
		return () => { alive = false }
	}, [])

	const selected = useMemo(() => {
		const codes = new Set(value || [])
		// A code the catalogue does not know is still shown as a chip.
		// Dropping it would silently edit the rule the moment someone
		// opened it — the operator has to see what is there to remove it.
		const known = items.filter(i => codes.has(i.code))
		const unknown = [...codes]
			.filter(c => !items.some(i => i.code === c))
			.map(c => ({ kind: 'unknown', code: c, name: c }))
		return [...known, ...unknown]
	}, [value, items])

	const offices = items.filter(i => i.kind === 'office')
	const unmapped = offices.filter(o => !o.mapped).length

	const disabledFor = (item) =>
		requireMapped && item.kind === 'office' && !item.mapped

	return (
		<Box sx={sx}>
			<Autocomplete
				multiple
				disableCloseOnSelect
				size="small"
				options={items}
				loading={state === 'loading'}
				value={selected}
				getOptionLabel={labelOf}
				isOptionEqualToValue={(a, b) => a.code === b.code}
				getOptionDisabled={disabledFor}
				groupBy={(o) => (o.kind === 'group' ? GROUPS : (o.city || 'Other'))}
				onChange={(_, picked) => onChange(picked.map(p => p.code))}
				renderInput={(params) => (
					<TextField {...params} label={label}
					           placeholder={placeholder || 'All offices'}/>
				)}
				renderOption={(props, option, { selected: isSel }) => {
					const off = disabledFor(option)
					const row = (
						<li {...props} key={option.code}>
							<Checkbox icon={<CheckBoxOutlineBlankIcon fontSize="small"/>}
							          checkedIcon={<CheckBoxIcon fontSize="small"/>}
							          checked={isSel} disabled={off}
							          sx={{ mr: 1, p: 0.25 }}/>
							<Box component="span"
							     sx={{ fontSize: 13,
							           color: off ? dsColorNew.textSubtle : 'inherit' }}>
								{labelOf(option)}
							</Box>
							{off && <BlockIcon sx={{ fontSize: 14, ml: 0.75,
							                         color: dsColorNew.textSubtle }}/>}
						</li>
					)
					return off ? (
						<Tooltip key={option.code}
						         title="Not mapped to Price Seeker, so no search can be launched for it. Map it in My Business → Admin → Offices.">
							<span>{row}</span>
						</Tooltip>
					) : row
				}}
				renderTags={(picked, getTagProps) => picked.map((o, i) => (
					<Chip {...getTagProps({ index: i })} key={o.code} size="small"
					      label={o.kind === 'group' ? `${o.code} ·` : o.code}
					      title={labelOf(o)}
					      color={o.kind === 'group' ? 'primary' : 'default'}
					      variant={o.kind === 'unknown' ? 'outlined' : 'filled'}/>
				))}
			/>

			{state === 'error' && (
				<AppNote severity="error" sx={{ mt: 1 }}>
					Could not load the office catalogue. Leaving this empty means
					every office, which may not be what you want — reload before
					saving.
				</AppNote>
			)}

			{helperText && state !== 'error' && (
				<Typography component="span"
				            sx={{ display: 'block', mt: 0.5, fontSize: 12, color: dsColorNew.textMuted }}>
					{helperText}
				</Typography>
			)}

			{/* Said once, under the field, with the number: an operator who
			    cannot pick the office they want deserves to know why before
			    they go looking for the bug. */}
			{requireMapped && state === 'ready' && unmapped > 0 && (
				<Typography component="span"
				            sx={{ display: 'block', mt: 0.5, fontSize: 12, color: dsColorNew.noteAmber }}>
					{unmapped} of {offices.length} offices cannot be searched yet:
					they have no Price Seeker mapping. Map them in My Business →
					Admin → Offices.
				</Typography>
			)}
		</Box>
	)
}
